/** Canonical constant for new callers: MAX_PAGE_SIZE. HTTP_MAX_LIMIT (public routes) is intentionally below it and ADMIN_MAX_PAGE_SIZE intentionally above. */

import { nonNegativeFiniteOrNull } from './number-guards.ts';

export const DEFAULT_PARALLEL_BATCH_SIZE = 10;

/**
 * How many PropertyDO reads a feed refresh runs at once, and therefore how many cards one WebSocket frame carries.
 *
 * The reads go to DISTINCT Durable Objects, so a narrow batch only stretched a search across more round trips -- at
 * the general default of 10, a 64-result search reached the browser over ~10 seconds, one frame per card. Wider than
 * the default and deliberately NOT unbounded: a 200-property feed still lands as a handful of frames, each one a
 * single store dispatch, rather than one unbounded burst of Durable Object RPCs from one UserDO.
 */
export const PROPERTY_FEED_REFRESH_CONCURRENCY = 25;

/**
 * Fan-out width for the LISTING-AGGREGATION lane specifically -- `agents-realauction-source-run`'s per-listing
 * `mapInBatches` over `mm-aggregate`.
 *
 * ITS OWN CONST RATHER THAN A LOWERED `DEFAULT_PARALLEL_BATCH_SIZE`, because the two answer different questions. The
 * default's other callers fan out over CHEAP elements -- one WS broadcast, one notification, one telemetry fetch, one
 * downloaded-doc cleanup -- where 10 is a sensible bound and lowering it just makes them slower. One element HERE is an
 * entire `mm-aggregate` graph: a geocode, a property find-or-create, the MM insert, `transaction-create`'s D1 batch and
 * the PropertyDO syncs. Ten of those in flight is ten concurrent multi-write D1 sessions against one database, which is
 * a different order of load from ten notifications.
 *
 * FOUR, at operator direction 2026-08-25, against witnessed failures where a source's listings stopped producing
 * transactions and `home_details` after the MM row was already created -- the `In Progress` half-built shape
 * `mm-aggregate` deliberately refuses on re-run.
 *
 * THIS IS A MITIGATION, NOT A DIAGNOSIS, and the distinction is worth keeping: the witnessed failure was per-SOURCE and
 * all-or-nothing (the first listing of a source fails, every later one fails the same way), which is the shape of
 * shared state poisoned once rather than of concurrent load. Narrowing the window makes the failure rarer without
 * establishing its cause. What blocks the diagnosis is that the fan-out DEGRADES instead of throwing --
 * `submitListing` returns `degradedOutcome('error', 'aggregate_flow_failed', ...)` and nothing logs the witness -- so
 * `browser-run-prod` recorded 65k events and ZERO errors across the window the failures happened in. Restoring a wider
 * width is safe only once that witness is visible.
 */
export const LISTING_AGGREGATION_BATCH_SIZE = 4;

/** Universal max page size for callers without an explicit context cap. */
export const MAX_PAGE_SIZE = 500;

export const PAGINATION_DEFAULTS = {
	limit: 50,
	offset: 0,
} as const;

export const HTTP_MAX_LIMIT = 200;

/** Maximum page size for admin API routes (overrides MAX_PAGE_SIZE for admin dashboards). */
export const ADMIN_MAX_PAGE_SIZE = 1000;

export const DEFAULT_STATUS_QUERY_LIMIT = 100;

/** Offset-pagination stride for the Zoho Desk `/tickets` family: `from` is 1-based and advances by exactly this many rows per page. */
export const ZOHO_DESK_PAGE_SIZE = 100;

/** Conversation-list page stride for the Connect desktop inbox (spa/connect); the pager advances the offset by exactly this many rows. */
export const CONNECT_INBOX_PAGE_SIZE = 50;

/** How many people the G4 control page's person search shows (`spa/connect-control`, `admin-users-list`). Small on
 *  purpose: it is a lookup an operator narrows by typing more, not a list anyone pages through. */
export const CONNECT_CONTROL_USER_SEARCH_LIMIT = 10;

// NO `CONNECT_SEARCH_LIMIT`. The Connect cross-corpus search cap was declared with no reader anywhere -- the search
// surface it was minted for binds its own limit -- so the const stated a decision nothing enforced. Re-add it beside
// the query that actually binds it.

/** Row cap for the recent-ledger-entries preview embedded in the admin offer detail response. Intentionally small: the detail UI shows a short timeline preview, not a full ledger paginator. */
export const ADMIN_OFFER_DETAIL_LEDGER_LIMIT = 10;

/** Row cap for the recent-offers preview embedded in the admin property detail response. Intentionally small: the detail UI shows a recent-offers preview, not a full offers paginator. */
export const ADMIN_PROPERTY_DETAIL_OFFERS_LIMIT = 20;

/** Row cap for the recent-ledger-entries embedded in the admin account detail response, bounded so a runaway ledger does not balloon the response body. */
export const ADMIN_ACCOUNT_DETAIL_LEDGER_LIMIT = 500;

/** Default page size for the bell-dropdown notifications list. */
export const DEFAULT_NOTIFICATIONS_LIMIT = 20;

/** Max page size for the notifications list endpoint (admin + explore). */
export const NOTIFICATIONS_MAX_LIMIT = 100;

/**
 * Photos per property kept in the recent-properties KV card cache (`buildRecentCardSlice`).
 *
 * This is the WHOLE image budget an explore card gets: a default feed load is served from the cache and nothing
 * upgrades it afterwards, so a card can only swipe through what this allows. It rides KV and then the socket to every
 * viewer for ~200 properties, which is why it is capped rather than unbounded.
 */
export const CARD_CACHE_IMAGE_LIMIT = 10;

/**
 * Shape version of a recent-properties KV card entry. BUMP IT whenever `buildRecentCardSlice` starts keeping a field it
 * used to drop.
 *
 * A carried-forward entry is re-sliced from the PREVIOUS CACHED VALUE, never from the PropertyDO, so widening the slice
 * cannot recover data an existing entry no longer holds -- re-slicing a one-image array to ten still yields one. Without
 * a version, `selectIdsToRead` only re-reads an entry once it ages past REFRESH_AGE_MS, so a widened field takes a full
 * rotation to appear and looks like the change never shipped. Entries stamped with an older version are treated as
 * maximally drifted (`cachedAt: 0`) so they re-read first, and the stamp makes that self-terminating.
 *
 * Version 2: `images_list` went from 1 photo to `CARD_CACHE_IMAGE_LIMIT`, restoring the feed carousel's swipe.
 */
export const CARD_CACHE_SHAPE_VERSION = 2;

/**
 * Non-negative integer row offset; any non-finite, negative or fractional input degrades to 0.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): head of the list-window clamp pair, homed in `const/` because a
 * `utils/` home is unreachable from a `utils/` SIBLING under `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, so every
 * utils-layer list normalizer would hand-roll its own clamp -- and a hand-roll that omits `Math.floor` passes a
 * fractional `2.5` straight into a SQL `OFFSET`. `const/` is reachable from every layer, so the copies collapse to one.
 * No consumer roster is carried here: the previous one named a leaf that had already been cut, and an import grep
 * answers the question without going stale. Paired forbid-rule: `QUAL-PAGINATION-CLAMP-INLINE-001`.
 */
export function clampOffset(raw: unknown): number {
	const n = nonNegativeFiniteOrNull(Number(raw));
	return n === null ? 0 : Math.floor(n);
}

/** Positive integer page size bounded by `max`; any non-finite or non-positive input degrades to `fallback`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned + ssot-derived): rides the same layer-floor home as
 *  {@link clampOffset}, and defaults from the same-file {@link PAGINATION_DEFAULTS} SSOT. */
export function clampLimit(raw: unknown, max: number, fallback: number = PAGINATION_DEFAULTS.limit): number {
	const n = Number(raw);
	if (!Number.isFinite(n) || n <= 0) return fallback;
	return Math.min(Math.floor(n), max);
}
